// Topic image mapping for class cards
// Maps topic keywords to specific generated images

const TOPIC_IMAGES = {
  // Animation / Creative
  animation: 'https://static.prod-images.emergentagent.com/jobs/919c286a-adec-4e0c-a5e7-ec3a45dcf900/images/e77847ab11e5292aec569a490e84254a15c2826e49fe438f87f929f65352fc44.png',
  // Game Development
  game: 'https://static.prod-images.emergentagent.com/jobs/919c286a-adec-4e0c-a5e7-ec3a45dcf900/images/d14f5e2c927334016b015a754405772205e709100102a6aef78708f45f803126.png',
  // Python / Coding
  python: 'https://static.prod-images.emergentagent.com/jobs/919c286a-adec-4e0c-a5e7-ec3a45dcf900/images/749809292f19eb20201d9f8b76b04f4c4afab7b1a7b4376bbd614c19acc59e62.png',
  // Robotics / STEM
  robotics: 'https://static.prod-images.emergentagent.com/jobs/919c286a-adec-4e0c-a5e7-ec3a45dcf900/images/652682230c100974a15fe0fd7a67bb45af18bbf825f90f8be2320694ce60549d.png',
  // Web Development
  web: 'https://static.prod-images.emergentagent.com/jobs/919c286a-adec-4e0c-a5e7-ec3a45dcf900/images/4d8bc5bd67a2949df0ddbb93d24ebc600ad9f79b394e71d9cace21e591db0799.png',
  // Talking Pet / Digital Pet
  pet: 'https://static.prod-images.emergentagent.com/jobs/919c286a-adec-4e0c-a5e7-ec3a45dcf900/images/a895124aa1a4227133a8895faac9b5b06e4e89a00ab6b8ab4666573f78449250.png',
  // Scratch Programming
  scratch: 'https://static.prod-images.emergentagent.com/jobs/919c286a-adec-4e0c-a5e7-ec3a45dcf900/images/d1a7e63b4652eea4f9e5af9abae9ece2510984804c7a495703a9a1d6bea56c1e.png',
  // Demo / General Education
  demo: 'https://static.prod-images.emergentagent.com/jobs/919c286a-adec-4e0c-a5e7-ec3a45dcf900/images/7f2a7eff8bc397ab732e9b0ec8e6c881928118a1757c204dd27d4fbfc1123c9b.png',
};

// Keyword-to-category mapping for intelligent matching
const KEYWORD_MAP = [
  { keywords: ['animated', 'animation', 'animate', 'name', 'sprite', 'character'], category: 'animation' },
  { keywords: ['maze', 'game', 'dino', 'jump', 'catcher', 'fruit', 'runner', 'pong', 'brick', 'breakout', 'snake', 'flappy', 'platformer', 'shooter'], category: 'game' },
  { keywords: ['python', 'variable', 'loop', 'function', 'list', 'dictionary', 'class', 'oop', 'algorithm', 'data structure', 'condition'], category: 'python' },
  { keywords: ['robot', 'robotics', 'arduino', 'sensor', 'motor', 'circuit', 'hardware', 'iot'], category: 'robotics' },
  { keywords: ['web', 'html', 'css', 'javascript', 'react', 'website', 'frontend', 'backend', 'api'], category: 'web' },
  { keywords: ['pet', 'talking', 'chatbot', 'virtual', 'digital pet', 'ai'], category: 'pet' },
  { keywords: ['scratch', 'block', 'drag', 'visual programming', 'blockly'], category: 'scratch' },
  { keywords: ['demo', 'trial', 'introduction', 'intro', 'orientation'], category: 'demo' },
  { keywords: ['coding', 'code', 'program', 'computer', 'software', 'tech'], category: 'python' },
];

/**
 * Get the appropriate topic image URL based on topic name or course name.
 * @param {string} topicName - The topic/class name
 * @param {string} courseName - The course/subject name (fallback)
 * @param {boolean} isDemo - Whether this is a demo class
 * @returns {string} Image URL
 */
export const getTopicImage = (topicName = '', courseName = '', isDemo = false) => {
  if (isDemo) return TOPIC_IMAGES.demo;

  const searchText = `${topicName} ${courseName}`.toLowerCase();

  for (const mapping of KEYWORD_MAP) {
    if (mapping.keywords.some(kw => searchText.includes(kw))) {
      return TOPIC_IMAGES[mapping.category];
    }
  }

  // Default fallback: coding/python image
  return TOPIC_IMAGES.python;
};

/**
 * Get the category label for a topic (used for the overlay badge)
 */
export const getTopicCategory = (topicName = '', courseName = '', isDemo = false) => {
  if (isDemo) return 'Demo';

  const searchText = `${topicName} ${courseName}`.toLowerCase();

  for (const mapping of KEYWORD_MAP) {
    if (mapping.keywords.some(kw => searchText.includes(kw))) {
      const labels = {
        animation: 'Animation',
        game: 'Game Dev',
        python: 'Python',
        robotics: 'Robotics',
        web: 'Web Dev',
        pet: 'Creative',
        scratch: 'Scratch',
        demo: 'Demo',
      };
      return labels[mapping.category] || 'Coding';
    }
  }

  return 'Coding';
};

/**
 * Get a kid-friendly gradient/color theme for a topic card.
 * Returns classes for: borderLeft accent, badge gradient, and icon background.
 */
export const getTopicColor = (topicName = '', courseName = '', isDemo = false) => {
  if (isDemo) return { accent: 'border-l-pink-500', bg: 'from-pink-100 to-rose-50', badge: 'bg-pink-500' };

  const searchText = `${topicName} ${courseName}`.toLowerCase();

  const COLOR_MAP = {
    animation: { accent: 'border-l-purple-500', bg: 'from-purple-100 to-fuchsia-50', badge: 'bg-purple-500' },
    game: { accent: 'border-l-orange-500', bg: 'from-orange-100 to-amber-50', badge: 'bg-orange-500' },
    python: { accent: 'border-l-blue-500', bg: 'from-blue-100 to-sky-50', badge: 'bg-blue-500' },
    robotics: { accent: 'border-l-cyan-500', bg: 'from-cyan-100 to-teal-50', badge: 'bg-cyan-500' },
    web: { accent: 'border-l-emerald-500', bg: 'from-emerald-100 to-green-50', badge: 'bg-emerald-500' },
    pet: { accent: 'border-l-pink-500', bg: 'from-pink-100 to-rose-50', badge: 'bg-pink-500' },
    scratch: { accent: 'border-l-yellow-500', bg: 'from-yellow-100 to-amber-50', badge: 'bg-yellow-500' },
    demo: { accent: 'border-l-indigo-500', bg: 'from-indigo-100 to-blue-50', badge: 'bg-indigo-500' },
  };

  for (const mapping of KEYWORD_MAP) {
    if (mapping.keywords.some(kw => searchText.includes(kw))) {
      return COLOR_MAP[mapping.category] || COLOR_MAP.python;
    }
  }
  return COLOR_MAP.python;
};

export default { getTopicImage, getTopicCategory, getTopicColor };
